import { useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import saveAs from 'file-saver';

import {
  useCreateBatchDownloadMutation,
  useGetBatchDownloadQuery,
  useUpdateBatchDownloadPdfInfoMutation
} from '@/store/slices/api/batchDownloadSlice';
import { useLazyGetVendorReportsQuery } from '@/store/slices/api/reportsApiSlice';
import { getCurrentDate, getEncodedDateTimeWithTimezone } from '@/utils/constants';
import { formatUtcDate } from '@/utils/formatDate';
import { routes } from '@/routes';

import { BatchJobStatus } from '@/types/batchDownload';
import { PatientData } from '@/types/patientList';
import { ReportType } from '@/types/transmissionReport';

const POLLING_INTERVAL = 2500;
const FILE_NAME_PREFIX = 'NotDownloaded_Reports';
const ALL_REPORTS_FILE_PREFIX = 'All_Reports';

interface BatchDownloadState {
  jobId: number;
  status: BatchJobStatus;
  errorMessage: string;
  showModal: boolean;
  isDownloading: boolean;
}

export const usePatientActions = (patient: PatientData, closeMenu: () => void) => {
  const [isDownloadingPrevious, setIsDownloadingPrevious] = useState(false);
  const [isDownloadingNotDownloaded, setIsDownloadingNotDownloaded] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [allReportIdsForDownload, setAllReportIdsForDownload] = useState<string[]>([]);

  const [batchDownloadState, setBatchDownloadState] = useState<BatchDownloadState>({
    jobId: 0,
    status: BatchJobStatus.INITIALIZED,
    errorMessage: '',
    showModal: false,
    isDownloading: false
  });

  const [allReportsBatchState, setAllReportsBatchState] = useState<BatchDownloadState>({
    jobId: 0,
    status: BatchJobStatus.INITIALIZED,
    errorMessage: '',
    showModal: false,
    isDownloading: false
  });

  const [getVendorReports] = useLazyGetVendorReportsQuery();
  const [createBatchDownload] = useCreateBatchDownloadMutation();
  const [updateBatchDownloadPdfInfo] = useUpdateBatchDownloadPdfInfoMutation();

  const { data: batchDownloadData } = useGetBatchDownloadQuery(batchDownloadState.jobId, {
    skip: !batchDownloadState.jobId || !batchDownloadState.showModal,
    pollingInterval:
      batchDownloadState.jobId &&
      batchDownloadState.showModal &&
      batchDownloadState.status !== BatchJobStatus.SUCCESS
        ? POLLING_INTERVAL
        : 0
  });

  const { data: allReportsBatchData } = useGetBatchDownloadQuery(allReportsBatchState.jobId, {
    skip: !allReportsBatchState.jobId || !allReportsBatchState.showModal,
    pollingInterval:
      allReportsBatchState.jobId &&
      allReportsBatchState.showModal &&
      allReportsBatchState.status !== BatchJobStatus.SUCCESS
        ? POLLING_INTERVAL
        : 0
  });

  const notDownloadedCount = patient.notDownloadedReports?.length || 0;
  const hasCurrentReport = Boolean(patient.currentReport?.id);
  const hasPreviousReport = Boolean(patient.previousReport?.id);

  useEffect(() => {
    if (batchDownloadData?.batchDownload) {
      setBatchDownloadState((prev) => ({
        ...prev,
        status: batchDownloadData.batchDownload.status || BatchJobStatus.INITIALIZED,
        jobId: batchDownloadData.batchDownload.jobId
      }));
    }
  }, [batchDownloadData]);

  useEffect(() => {
    if (allReportsBatchData?.batchDownload) {
      setAllReportsBatchState((prev) => ({
        ...prev,
        status: allReportsBatchData.batchDownload.status || BatchJobStatus.INITIALIZED,
        jobId: allReportsBatchData.batchDownload.jobId
      }));
    }
  }, [allReportsBatchData]);

  const showNotification = (type: 'success' | 'error', message: string, title?: string) => {
    notifications.show({
      title: title || (type === 'success' ? 'Success' : 'Error'),
      message,
      color: type === 'success' ? 'green' : 'red',
      autoClose: 4000
    });
  };

  const getReportDownloadName = (
    reportType: string,
    givenName: string,
    familyName: string,
    transmissionDate: string
  ): string => {
    try {
      const formattedDate = formatUtcDate(transmissionDate, 'MM_DD_YYYY');
      const baseName = `${givenName}_${familyName}_${formattedDate}`;

      const reportTypeMap = {
        [ReportType.InClinic]: 'InClinic',
        [ReportType.Manual]: 'Manual',
        [ReportType.Remote]: 'Remote',
        [ReportType.RemoteInClinic]: 'RemoteInClinic'
      };

      const type = reportTypeMap[reportType as keyof typeof reportTypeMap] || 'Rhythm';
      return `${baseName}_${type}_Report.pdf`;
    } catch (error) {
      console.error('Error generating filename:', error);
      return `Rhythm_Report_${Date.now()}.pdf`;
    }
  };

  const findSignedReport = (vendorReports: any[], patientMrn: string) => {
    const pdfReports = vendorReports.filter(
      (report) => report.name.split('.').pop()?.toLowerCase() === 'pdf'
    );

    const patientSpecificReports = pdfReports.filter((report) => report.name.includes(patientMrn));

    const signedReports = pdfReports.filter((report) =>
      ['Signed_Rhythm_Report', 'RoutineReport', 'signed'].some((keyword) =>
        report.name.includes(keyword)
      )
    );

    return (
      patientSpecificReports.find((report) =>
        ['Signed', 'signed'].some((keyword) => report.name.includes(keyword))
      ) ||
      signedReports[0] ||
      patientSpecificReports[0] ||
      pdfReports[0]
    );
  };

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    closeMenu();
  };

  const handleAsyncDownload = async (
    downloadFn: () => Promise<{ success: boolean; message?: string }>,
    setLoading: (loading: boolean) => void
  ) => {
    setLoading(true);
    try {
      const result = await downloadFn();

      if (result.success) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
      } else {
        showNotification('error', result.message || 'Download failed');
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    } catch (error) {
      showNotification('error', 'An unexpected error occurred during download');
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } finally {
      setLoading(false);
      setTimeout(closeMenu, 300);
    }
  };

  const onViewReport = () => {
    if (hasCurrentReport) {
      const reportUrl = routes.physician.transmissionReportDetail(
        patient.currentReport!.id.toString()
      );
      openInNewTab(reportUrl);
    }
  };

  const onEditReport = () => {
    if (hasCurrentReport) {
      const editUrl = routes.physician.transmissionReportDetail(
        patient.currentReport!.id.toString()
      );
      openInNewTab(editUrl);
    }
  };

  const onViewPreviousReport = () => {
    if (hasPreviousReport) {
      const previousReportUrl = routes.physician.transmissionReportDetail(
        patient.previousReport!.id.toString()
      );
      openInNewTab(previousReportUrl);
    }
  };

  const handleClose = () => {
    setBatchDownloadState((prev) => ({
      ...prev,
      showModal: false
    }));
    setIsDownloadingNotDownloaded(false);
  };

  const handleAllReportsClose = () => {
    setAllReportsBatchState((prev) => ({
      ...prev,
      showModal: false
    }));
    setIsDownloadingAll(false);
  };

  const onDownloadPreviousReport = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!hasPreviousReport || !patient.previousReport || isDownloadingPrevious) return;

    const downloadPreviousReport = async (): Promise<{ success: boolean; message?: string }> => {
      try {
        const { data: vendorReportData, isError } = await getVendorReports(
          parseInt(patient.previousReport!.id)
        );

        if (isError || !vendorReportData?.vendorTransmissionReports?.length) {
          return {
            success: false,
            message: 'No vendor transmission reports found for this patient'
          };
        }

        const sortedReports = [...vendorReportData.vendorTransmissionReports].sort(
          (a, b) => dayjs(b.lastModified).valueOf() - dayjs(a.lastModified).valueOf()
        );

        const signedReport = findSignedReport(sortedReports, patient.mrn);

        if (!signedReport?.pdfUrl) {
          return {
            success: false,
            message: 'No signed report available for download'
          };
        }

        const response = await fetch(signedReport.pdfUrl);
        if (!response.ok) {
          return {
            success: false,
            message: `Failed to download PDF: ${response.statusText}`
          };
        }

        const blob = await response.blob();
        const [givenName, ...familyNameParts] = patient.name.split(' ');
        const familyName = familyNameParts.join(' ');

        const filename = getReportDownloadName(
          patient.previousReport!.reportType || ReportType.Remote,
          givenName || '',
          familyName || '',
          patient.previousReport!.transmissionAt
        );

        saveAs(blob, filename);
        return { success: true };
      } catch (error) {
        console.error('Download error:', error);
        return {
          success: false,
          message: 'Failed to download report. Please try again.'
        };
      }
    };

    await handleAsyncDownload(downloadPreviousReport, setIsDownloadingPrevious);
  };

  const onDownloadNotDownloadedReports = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (notDownloadedCount === 0 || isDownloadingNotDownloaded) return;

    setIsDownloadingNotDownloaded(true);
    setBatchDownloadState((prev) => ({ ...prev, showModal: true }));

    try {
      const transmissiondata =
        patient.notDownloadedReports?.map((id) => ({
          transmissionId: parseInt(id)
        })) || [];

      const response = await createBatchDownload({ transmissiondata });
      const jobId = response.data?.jobId || 0;

      setBatchDownloadState((prev) => ({
        ...prev,
        jobId,
        status: jobId ? BatchJobStatus.PROCESSING : BatchJobStatus.FAILED,
        errorMessage: jobId ? '' : 'Failed to create batch download job'
      }));
    } catch (error) {
      setBatchDownloadState((prev) => ({
        ...prev,
        status: BatchJobStatus.FAILED,
        errorMessage: error instanceof Error ? error.message : 'Failed to start download'
      }));
      setIsDownloadingNotDownloaded(false);
    }
  };

  const onDownloadAllReports = async (allReportIds: string[]) => {
    if (allReportIds.length === 0 || isDownloadingAll) return;

    setIsDownloadingAll(true);
    setAllReportsBatchState((prev) => ({ ...prev, showModal: true }));
    setAllReportIdsForDownload(allReportIds);

    try {
      const transmissiondata = allReportIds.map((id) => ({
        transmissionId: parseInt(id)
      }));

      const response = await createBatchDownload({ transmissiondata });
      const jobId = response.data?.jobId || 0;

      setAllReportsBatchState((prev) => ({
        ...prev,
        jobId,
        status: jobId ? BatchJobStatus.PROCESSING : BatchJobStatus.FAILED,
        errorMessage: jobId ? '' : 'Failed to create batch download job'
      }));
    } catch (error) {
      setAllReportsBatchState((prev) => ({
        ...prev,
        status: BatchJobStatus.FAILED,
        errorMessage: error instanceof Error ? error.message : 'Failed to start download'
      }));
      setIsDownloadingAll(false);
    }
  };

  const handleBatchDownload = async () => {
    if (!batchDownloadData?.batchDownload?.s3SignedUrl) return;

    setBatchDownloadState((prev) => ({ ...prev, isDownloading: true }));

    try {
      const response = await fetch(batchDownloadData.batchDownload.s3SignedUrl);
      const blob = await response.blob();

      const transmissionReportIds = patient.notDownloadedReports?.map((id) => parseInt(id)) || [];
      await updateBatchDownloadPdfInfo({
        tranmissionReportIds: transmissionReportIds,
        downloadDate: getEncodedDateTimeWithTimezone()
      });

      const patientName = patient.name.replace(/\s+/g, '_');
      saveAs(blob, `${FILE_NAME_PREFIX}_${patientName}_${getCurrentDate('MM.DD.YYYY')}.zip`);

      showNotification(
        'success',
        `${patient.notDownloadedReports?.length || 0} reports downloaded successfully`
      );

      setBatchDownloadState({
        jobId: 0,
        status: BatchJobStatus.INITIALIZED,
        errorMessage: '',
        showModal: false,
        isDownloading: false
      });

      setIsDownloadingNotDownloaded(false);
      setTimeout(closeMenu, 300);
    } catch (error) {
      setBatchDownloadState((prev) => ({
        ...prev,
        status: BatchJobStatus.FAILED,
        errorMessage: error instanceof Error ? error.message : 'Download failed',
        isDownloading: false
      }));
    }
  };

  const handleAllReportsBatchDownload = async () => {
    if (!allReportsBatchData?.batchDownload?.s3SignedUrl) return;

    setAllReportsBatchState((prev) => ({ ...prev, isDownloading: true }));

    try {
      const response = await fetch(allReportsBatchData.batchDownload.s3SignedUrl);
      const blob = await response.blob();

      const transmissionReportIds = allReportIdsForDownload.map((id) => parseInt(id));
      await updateBatchDownloadPdfInfo({
        tranmissionReportIds: transmissionReportIds,
        downloadDate: getEncodedDateTimeWithTimezone()
      });

      const patientName = patient.name.replace(/\s+/g, '_');
      saveAs(blob, `${ALL_REPORTS_FILE_PREFIX}_${patientName}_${getCurrentDate('MM.DD.YYYY')}.zip`);

      showNotification(
        'success',
        `${transmissionReportIds.length} reports downloaded successfully`
      );

      setAllReportsBatchState({
        jobId: 0,
        status: BatchJobStatus.INITIALIZED,
        errorMessage: '',
        showModal: false,
        isDownloading: false
      });

      setIsDownloadingAll(false);
      setAllReportIdsForDownload([]);
      setTimeout(closeMenu, 300);
    } catch (error) {
      setAllReportsBatchState((prev) => ({
        ...prev,
        status: BatchJobStatus.FAILED,
        errorMessage: error instanceof Error ? error.message : 'Download failed',
        isDownloading: false
      }));
    }
  };

  return {
    isDownloadingPrevious,
    isDownloadingNotDownloaded,
    isDownloadingAll,
    batchDownloadState,
    setBatchDownloadState,
    allReportsBatchState,
    setAllReportsBatchState,
    hasCurrentReport,
    hasPreviousReport,
    notDownloadedCount,
    onViewReport,
    onEditReport,
    onViewPreviousReport,
    onDownloadPreviousReport,
    onDownloadNotDownloadedReports,
    onDownloadAllReports,
    handleBatchDownload,
    handleAllReportsBatchDownload,
    handleClose,
    handleAllReportsClose
  };
};
